import React from 'react';
import {
  ZONE_COLORS,
  ZONE_LABELS,
  ZONE_DESCRIPTIONS,
} from '../../../utils/patentZones';
import { moleculePatentCache, currentCollisionMapFileId } from '../PatentCollisionMap';

interface PatentsTabProps {
  smiles: string;
  enrichedZone?: number | null;
}

const PatentsTab: React.FC<PatentsTabProps> = ({ smiles, enrichedZone }) => {
  const cacheKey = (smiles && currentCollisionMapFileId != null) ? `${currentCollisionMapFileId}:${smiles}` : null;
  const cached = cacheKey ? moleculePatentCache.get(cacheKey) : undefined;

  // No file scan data available
  if (!cached) {
    const fallbackZone = (enrichedZone != null && enrichedZone >= 1 && enrichedZone <= 5) ? enrichedZone : null;
    return (
      <div className="space-y-4">
        {fallbackZone && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${ZONE_COLORS[fallbackZone]}15`, border: `1px solid ${ZONE_COLORS[fallbackZone]}30` }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ZONE_COLORS[fallbackZone] }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Zone {fallbackZone}: {ZONE_LABELS[fallbackZone]}
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{ZONE_DESCRIPTIONS[fallbackZone]}</p>
          </div>
        )}
        <div className="text-center py-6">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Run patent scan from file toolbar to see detailed results
          </p>
        </div>
      </div>
    );
  }

  const { zone, patent_count_exact, patent_count_similar, max_similarity } = cached;
  const displayZone = (enrichedZone != null && enrichedZone >= 1 && enrichedZone <= 5)
    ? Math.min(enrichedZone, zone)
    : zone;

  return (
    <div className="space-y-5">
      {/* Section: Patent Landscape */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <svg className="w-4 h-4" style={{ color: '#0d9488' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
            Patent Landscape
          </h4>
        </div>
        <p className="text-[10px] mb-3" style={{ color: 'var(--text-muted)' }}>
          From cached file scan (28.4M SureChEMBL patent compounds)
        </p>

        {/* Zone summary card */}
        <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: `${ZONE_COLORS[displayZone]}15`, border: `1px solid ${ZONE_COLORS[displayZone]}30` }}>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ZONE_COLORS[displayZone] }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Zone {displayZone}: {ZONE_LABELS[displayZone]}
            </span>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{ZONE_DESCRIPTIONS[displayZone]}</p>
        </div>

        {/* Stats */}
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Exact patent matches</span>
            <span className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{patent_count_exact}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Similar patent matches</span>
            <span className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{patent_count_similar}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Max similarity</span>
            <span className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
              {max_similarity != null ? `${(max_similarity * 100).toFixed(1)}%` : '\u2014'}
            </span>
          </div>
        </div>

        {/* No conflicts */}
        {patent_count_exact === 0 && patent_count_similar === 0 && (
          <div className="text-center py-4 mt-3">
            <p className="text-sm" style={{ color: '#22c55e' }}>No patent conflicts found</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>This compound appears to have freedom to operate</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatentsTab;
